import React, { useEffect, useState } from "react";
import { DatePicker, Modal, Select, TimePicker } from "antd";
import { Button, Form } from "antd";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { InputNumber } from "antd";
import { useSelector, useDispatch } from "react-redux";
import { getUserWorkLocationsHelper } from "../../../utils/helpers/workLocationsHelper";

import { ReactComponent as RepeatIcon } from "../../../assets/images/icons/repeat.svg";
import "./AddShiftModal.scss";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { ShiftType, ShiftTypePanorama } from "../../../Pages/Shifts/utils";
import DaySelector from "./Components/DaySelector/DaySelector";
import MyMultiDatePicker from "./Components/MultiDayPicker/MultiDayPicker";

dayjs.extend(utc);
dayjs.extend(timezone);

const { Option } = Select;

export enum TimeFrame {
  WEEK = "week",
  MONTH = "month",
}

type ShiftFormValues = {
  date: any;
  startTime: dayjs.Dayjs;
  endTime: dayjs.Dayjs;
  shiftType: ShiftType;
  timeFrame?: any;
  excludedDates?: any;
  excludedWeekDays?: any;
  isMultipleShift: boolean;
  leavePayPercentage?: any;
  durationInHours?: any;
  leavePayType?: any;
  locationId?: string;
};

type Props = {
  isOpen: boolean;
  onCloseModal: () => void;
  onAddShift: (values: ShiftFormValues) => void;
  shiftModalOpen: any;
  defaultShiftType?: ShiftTypePanorama;
};

const AddShiftModal: React.FC<Props> = ({
  isOpen,
  onCloseModal,
  onAddShift,
  shiftModalOpen,
  defaultShiftType,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const userData = useSelector((state: any) => state.userData.user);
  const workLocationsState = useSelector((state: any) => state.workLocations);
  const [form] = Form.useForm(); // Create a form instance using Form.useForm()
  const [isMultipleShift, setMultipleShift] = useState<boolean>(false);
  const [excludedRandomDates, setexcludedRandomDates] = useState<Dayjs[]>([]);
  const [excludedWeekDays, setExcludedWeekDays] = useState<number[]>([]);
  const [customHours, setCustomHours] = useState<number | null>(null);
  const [customMinutes, setCustomMinutes] = useState<number>(0);
  const [selectedLeavePayType, setSelectedLeavePayType] = useState<
    string | null
  >(null);
  const [userWorkLocations, setUserWorkLocations] = useState<any[]>([]);
  const [loadingLocations, setLoadingLocations] = useState<boolean>(false);

  const [selectedShiftType, setSelectedShiftType] =
    useState<ShiftTypePanorama | null>(null);
  const [useCustom, setUseCustom] = useState(false);

  useEffect(() => {
    // Load user work locations when modal opens
    const loadUserWorkLocations = async () => {
      if (shiftModalOpen?.user?._id && userData?.accessToken) {
        setLoadingLocations(true);
        try {
          const locations = await getUserWorkLocationsHelper(
            shiftModalOpen.user._id,
            userData.accessToken,
            dispatch,
            workLocationsState,
          );
          setUserWorkLocations(locations);

          // If user has only one work location, set it as default
          if (locations.length === 1) {
            form.setFieldsValue({ locationId: locations[0]._id });
          }
        } catch (error) {
          console.error("Error loading work locations:", error);
          setUserWorkLocations([]);
        } finally {
          setLoadingLocations(false);
        }
      }
    };

    if (isOpen) {
      loadUserWorkLocations();
    }

    // Reset form fields
    form.resetFields();
    if (defaultShiftType) {
      form.setFieldsValue({ shiftType: defaultShiftType });
      setSelectedShiftType(defaultShiftType);
    }
  }, [form, isOpen, shiftModalOpen, userData, dispatch, workLocationsState]);
  const handleOk = () => {
    onCloseModal();
  };
  const handleCancel = () => {
    onCloseModal();
  };
  const onFinish = async (values: any) => {
    const { date, startTime, duration, shiftType, timeFrame, locationId } =
      values;
    const newDate = dayjs.utc(date).add(2, "hour");

    // ako je LEAVES — računamo datume odsustva
    if (shiftType === ShiftTypePanorama.LEAVES) {
      const durationInHours =
        values.leavePayType === ShiftTypePanorama.HOURS
          ? Number(values.durationInHours) || 0
          : 0;
      const numberOfDays =
        shiftType === ShiftTypePanorama.LEAVES
          ? Number(values.leaveDays) || 1
          : Number(values.duration) || 1;
      const datesArray: Dayjs[] = [];
      const leaveType = values.leaveType || "Neodređeno";
      const leavePayPercentage = values.leavePayPercentage || 0;
      const leavePayType = values.leavePayType || ShiftTypePanorama.DAILY_WAGE;

      for (let i = 0; i < numberOfDays; i++) {
        datesArray.push(dayjs(newDate).add(i, "day"));
      }

      onAddShift({
        date: dayjs(newDate),
        startTime: dayjs("00:00", "HH:mm"),
        endTime: dayjs("23:59", "HH:mm"),
        shiftType,
        timeFrame: leaveType, // ⚡️ i dalje koristimo ovo polje za LEAVE TYPE
        excludedDates: datesArray,
        excludedWeekDays: [],
        isMultipleShift: true,
        leavePayPercentage, // ⚡️ šaljemo kao dodatno polje
        leavePayType, // ⚡️ šaljemo kao dodatno polje
        durationInHours,
        locationId,
      });
      return;
    }

    // za sve ostale tipove — standardno
    const fullStart = dayjs(date)
      .hour(startTime.hour())
      .minute(startTime.minute());

    const durationMinutes = useCustom
      ? Math.max(0, Math.round((customHours ?? 0) * 60 + (customMinutes || 0)))
      : Number(values.duration ?? 0);

    if (!durationMinutes || durationMinutes <= 0) {
      console.error("Invalid duration");
      return;
    }

    const fullEnd = fullStart.add(durationMinutes, "minute");

    onAddShift({
      date: dayjs(newDate),
      startTime: fullStart,
      endTime: fullEnd,
      shiftType,
      timeFrame,
      excludedDates: excludedRandomDates,
      excludedWeekDays,
      isMultipleShift,
      locationId,
    });
  };

  const onFinishFailed = (errorInfo: any) => {
    console.error("Failed:", errorInfo);
  };

  const generateDurationOptions = () => {
    const options = [];
    for (let minutes = 30; minutes <= 720; minutes += 30) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      const label = `${String(hours).padStart(2, "0")}:${String(mins).padStart(
        2,
        "0",
      )}`;
      options.push({ value: minutes, label });
    }
    return options;
  };

  return (
    <Modal
      title={
        <div className="addShiftHeader">
          <div className="col">
            <div className="profileImg">
              {shiftModalOpen?.user?.avatar?.url ? (
                <img
                  src={shiftModalOpen?.user?.avatar?.url ?? ""}
                  alt="Profile"
                />
              ) : (
                <div className="initials">
                  {`${shiftModalOpen?.user?.firstName?.[0] || ""}${
                    shiftModalOpen?.user?.lastName?.[0] || ""
                  }`}
                </div>
              )}
            </div>
            <div className="info">
              <div className="name">
                {shiftModalOpen?.user?.firstName}{" "}
                {shiftModalOpen?.user?.lastName}
              </div>
              <div className="date">
                {shiftModalOpen?.date ? format(shiftModalOpen.date, "MM.dd.yyyy") : format(new Date(), "MM.dd.yyyy")}
              </div>
            </div>
          </div>
          <div
            className={`col repeat ${isMultipleShift ? "active" : ""}`}
            onClick={() => setMultipleShift(!isMultipleShift)}
          >
            <RepeatIcon />
          </div>
        </div>
      }
      open={isOpen}
      onOk={handleOk}
      className="addShiftModal"
      onCancel={handleCancel}
      footer={[]}
    >
      <div className="customForm">
        <Form
          name="basic"
          form={form}
          labelCol={{ span: 8 }}
          //   wrapperCol={{ span: 16 }}
          layout="vertical"
          //   style={{ maxWidth: 600 }}
          //   initialValues={{ remember: true }}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
          onValuesChange={(changedValues, allValues) => {
            const date = allValues.date;

            if (changedValues.shiftType) {
              setSelectedShiftType(changedValues.shiftType);
              if (changedValues.shiftType === ShiftTypePanorama.DAILY_WAGE) {
                form.setFieldsValue({ duration: 510 }); // 8.5h = 510min
              } else if (
                changedValues.shiftType === ShiftTypePanorama.TEMPORARY_HELP
              ) {
                form.setFieldsValue({ duration: 240 }); // 4h = 240min
              }
            }

            if (changedValues.startTime && date) {
              const newStart = dayjs(date)
                .hour(changedValues.startTime.hour())
                .minute(changedValues.startTime.minute());
              form.setFieldsValue({ startTime: newStart });
            }

            if (changedValues.endTime && date) {
              const newEnd = dayjs(date)
                .hour(changedValues.endTime.hour())
                .minute(changedValues.endTime.minute());
              form.setFieldsValue({ endTime: newEnd });
            }
          }}
        >
          <div className="shiftModalGlobalItems">
            <Form.Item
              label={t("Date")}
              name="date"
              rules={[{ required: true, message: t("Please select date") }]}
              initialValue={
                shiftModalOpen?.date ? dayjs(shiftModalOpen?.date) : undefined
              }
            >
              <DatePicker
                style={{ width: "100%" }}
                format="YYYY-MM-DD"
                placeholder={t("Select date")}
              />
            </Form.Item>

            <div
              className={
                selectedShiftType === ShiftTypePanorama.LEAVES ||
                selectedShiftType === ShiftTypePanorama.DAILY_WAGE ||
                selectedShiftType === ShiftTypePanorama.TEMPORARY_HELP
                  ? "hiddenStartTimeAndDuration"
                  : ""
              }
            >
              <Form.Item
                label={t("Start Time")}
                name="startTime"
                initialValue={dayjs("07:00", "HH:mm")} // 👈 default vreme
                rules={[
                  {
                    required: true,
                    message: t("Please select start time"),
                  },
                ]}
              >
                <TimePicker
                  format="HH:mm"
                  style={{ width: "100%" }}
                  placeholder={t("Select time")}
                  minuteStep={15}
                  showNow={false} // bez "Now" dugmeta
                  onSelect={(time) => {
                    form.setFieldsValue({ startTime: time }); // odmah setuje
                  }}
                />
              </Form.Item>
              {/* <Form.Item
              label={t("End Time")}
              name="endTime"
              rules={[{ required: true, message: t("Please select end time") }]}
            >
              <TimePicker
                format="HH:mm"
                style={{ width: "100%" }}
                placeholder={t("Select time")}
              />
            </Form.Item> */}

              <Form.Item
                label={t("Duration")}
                name="duration"
                rules={
                  selectedShiftType === ShiftTypePanorama.LEAVES
                    ? [] // ⚡️ bez validacije kad je LEAVES
                    : [
                        {
                          required: true,
                          message: t("Please select or enter duration"),
                        },
                        {
                          validator: (_, value) => {
                            if (useCustom) {
                              const totalMinutes =
                                (customHours || 0) * 60 + (customMinutes || 0);
                              if (
                                totalMinutes <= 0 ||
                                customMinutes < 0 ||
                                customMinutes >= 60
                              ) {
                                return Promise.reject(
                                  new Error(t("Enter valid duration")),
                                );
                              }
                            }
                            return Promise.resolve();
                          },
                        },
                      ]
                }
              >
                {!useCustom ? (
                  <Select
                    style={{ width: "100%" }}
                    placeholder={t("Select duration")}
                    onChange={(val) => {
                      if (val === "custom") {
                        setUseCustom(true);
                        form.setFieldsValue({ duration: undefined });
                      } else {
                        setUseCustom(false);
                        form.setFieldsValue({ duration: val }); // već u minutima
                      }
                    }}
                  >
                    {generateDurationOptions().map((opt) => (
                      <Option key={opt.value} value={opt.value}>
                        {opt.label}
                      </Option>
                    ))}
                    <Option value="custom">{t("Custom (hours)...")}</Option>
                  </Select>
                ) : (
                  <div style={{ display: "flex", gap: "8px" }}>
                    <InputNumber
                      min={0}
                      max={300}
                      step={1}
                      precision={0}
                      style={{ width: "100%" }}
                      placeholder={t("Enter duration in hours")}
                      value={customHours ?? undefined}
                      onChange={(val) => {
                        const hoursVal = typeof val === "number" ? val : 0;
                        setCustomHours(val ?? null);
                        const minutesTotal = Math.round(
                          hoursVal * 60 + (customMinutes || 0),
                        );
                        form.setFieldsValue({
                          duration: minutesTotal > 0 ? minutesTotal : undefined,
                        });
                      }}
                      addonAfter={t("hours")}
                    />
                    <InputNumber
                      min={0}
                      max={59}
                      step={1}
                      precision={0}
                      style={{ width: "100%" }}
                      placeholder={t("Enter minutes")}
                      value={customMinutes}
                      onChange={(val) => {
                        const minutesVal = typeof val === "number" ? val : 0;
                        setCustomMinutes(minutesVal);
                        const minutesTotal = Math.round(
                          (customHours || 0) * 60 + minutesVal,
                        );
                        form.setFieldsValue({
                          duration: minutesTotal > 0 ? minutesTotal : undefined,
                        });
                      }}
                      addonAfter={t("minutes")}
                    />
                    <Button
                      type="default"
                      onClick={() => {
                        setUseCustom(false);
                        setCustomHours(null);
                        setCustomMinutes(0);
                        form.setFieldsValue({ duration: undefined });
                      }}
                    >
                      {t("Back")}
                    </Button>
                  </div>
                )}
              </Form.Item>
            </div>

            <Form.Item
              label={t("Shift Type")}
              name="shiftType"
              rules={[
                { required: true, message: t("Please select shift type") },
              ]}
            >
              <Select style={{ width: "100%" }}>
                <Option value={ShiftTypePanorama.DAILY_WAGE}>
                  {t("Daily Wage")}
                </Option>
                <Option value={ShiftTypePanorama.TEMPORARY_HELP}>
                  {t("Temporary Help")}
                </Option>
                <Option value={ShiftTypePanorama.HOURS}>{t("Hours")}</Option>
                <Option value={ShiftTypePanorama.LEAVES}>{t("Leaves")}</Option>
              </Select>
              {/* <Select style={{ width: "100%" }}>
                <Option value={ShiftType.MORNING}>{t("Morning Shift")}</Option>
                <Option value={ShiftType.AFTERNOON}>
                  {t("Afternoon Shift")}
                </Option>
                <Option value={ShiftType.NIGHT}>{t("Night Shift")}</Option>
              </Select> */}
            </Form.Item>

            {/* Location field - only show if user has work locations */}
            {userWorkLocations.length > 0 && (
              <Form.Item
                label={t("Work Location")}
                name="locationId"
                rules={
                  userWorkLocations.length > 1
                    ? [
                        {
                          required: true,
                          message: t("Please select work location"),
                        },
                      ]
                    : []
                }
              >
                <Select
                  style={{ width: "100%" }}
                  placeholder={t("Select work location")}
                  loading={loadingLocations}
                  disabled={userWorkLocations.length === 1}
                >
                  {userWorkLocations.map((location: any) => (
                    <Option key={location._id} value={location._id}>
                      {location.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            {selectedShiftType === ShiftTypePanorama.LEAVES && (
              <Form.Item
                label={t("Number of leave days")}
                name="leaveDays"
                rules={[
                  { required: true, message: t("Please enter number of days") },
                  {
                    validator: (_, value) => {
                      if (value <= 0)
                        return Promise.reject(t("Enter a valid number"));
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber
                  min={1}
                  max={30}
                  step={1}
                  style={{ width: "100%" }}
                  placeholder={t("Enter number of days")}
                  onChange={(val) => setMultipleShift(true)}
                />
              </Form.Item>
            )}

            {selectedShiftType === ShiftTypePanorama.LEAVES && (
              <>
                <Form.Item
                  label={t("Leave Type")}
                  name="leaveType"
                  rules={[
                    { required: true, message: t("Please select leave type") },
                  ]}
                >
                  <Select placeholder={t("Select leave type")}>
                    <Option value="Bolestan">{t("Bolestan")}</Option>
                    <Option value="Godisnji">{t("Godišnji")}</Option>
                    <Option value="Neplaceno">{t("Neplaćeno")}</Option>
                  </Select>
                </Form.Item>

                <Form.Item
                  label={t("Leave Pay Percentage")}
                  name="leavePayPercentage"
                  rules={[
                    { required: true, message: t("Please enter percentage") },
                    {
                      validator: (_, value) => {
                        if (value < 0 || value > 100) {
                          return Promise.reject(
                            t("Enter valid percentage (0–100)"),
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <InputNumber
                    min={0}
                    max={100}
                    step={5}
                    style={{ width: "100%" }}
                    placeholder={t("Enter percentage")}
                    addonAfter="%"
                  />
                </Form.Item>

                <Form.Item
                  label={t("Leave Pay Type")}
                  name="leavePayType"
                  rules={[
                    { required: true, message: t("Please select pay type") },
                  ]}
                >
                  <Select
                    placeholder={t("Select pay type")}
                    onChange={(val) => setSelectedLeavePayType(val)}
                  >
                    <Option value={ShiftTypePanorama.DAILY_WAGE}>
                      {t("Daily Wage")}
                    </Option>
                    <Option value={ShiftTypePanorama.HOURS}>
                      {t("Hours")}
                    </Option>
                  </Select>
                </Form.Item>

                {selectedLeavePayType === ShiftTypePanorama.HOURS && (
                  <Form.Item
                    label={t("Duration in hours")}
                    name="durationInHours"
                    rules={[
                      {
                        required: true,
                        message: t("Please enter duration in hours"),
                      },
                      {
                        validator: (_, value) => {
                          if (value <= 0)
                            return Promise.reject(
                              t("Enter a valid number of hours"),
                            );
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      min={1}
                      max={24}
                      step={0.5}
                      precision={1}
                      style={{ width: "100%" }}
                      placeholder={t("Enter number of hours (e.g. 8)")}
                    />
                  </Form.Item>
                )}
              </>
            )}
          </div>
          {isMultipleShift && (
            <div className="multipleShiftItems">
              <>
                <Form.Item label={t("Repeat")} name="repat" rules={[]}>
                  <div className="repeatShift">
                    <DaySelector
                      excludedWeekDays={excludedWeekDays}
                      setExcludedWeekDays={(val) => setExcludedWeekDays(val)}
                    />
                  </div>
                </Form.Item>
              </>
              <MyMultiDatePicker
                date={shiftModalOpen?.date}
                setSelectedDates={(dates: any) => setexcludedRandomDates(dates)}
                selectedDates={excludedRandomDates}
              />
              {selectedShiftType !== ShiftTypePanorama.LEAVES && (
                <Form.Item
                  label={t("Time Frame")}
                  name="timeFrame"
                  rules={[
                    {
                      required: isMultipleShift,
                      message: t("Please select time frame"),
                    },
                  ]}
                >
                  <Select style={{ width: "100%" }}>
                    <Option value={TimeFrame.WEEK}>{t("Week")}</Option>
                    <Option value={TimeFrame.MONTH}>{t("Month")}</Option>
                  </Select>
                </Form.Item>
              )}
            </div>
          )}
          <div className="saveShift">
            <Form.Item>
              <Button type="primary" htmlType="submit">
                {t("Save")}
              </Button>
            </Form.Item>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default AddShiftModal;
